import $ from 'jquery';
import _ from 'lodash';
import moment from 'moment';
import Helpers from '../utilities/helpers';
import DATA_CONFIG from '../config';

const CONFIG = {
    CONTENTS: '.js-reportsActivity-contents',
    LOAD_MORE_BTN: '.js-reportsActivity-btn',
    PAGE_LOAD_DEFAULT: 5,
    VIEW_MORE_COUNT: 10,
    TRANSITION_LENGTH: 150,
    NO_RESULTS: '<div class="copy copy_secondary u-caps u-center">No more activity found</div>'
};

/**
 * @fileOverview View handles updating the visual elements on the page
 */
export default class ReportsActivityView {
    /**
     * Construct the view and run initialization
     *
     * @param element
     */
    constructor(element) {
        this.selector = element;
        /**
         * Check to see if elements are passed in as jQuery objects or strings
         */
        this.$element = (element instanceof $) ? element : $(element);

        /**
         * If element doesn't exist, exit early
         */
        if (!this.$element.length) {
            return;
        }

        /**
         * sets handlebars templates up as variables to be used to build the result list
         */
        this.reportsActivityTemplate = window.JST['templates/JST/reportsActivityItem'];

        /**
         * listOfCenters is JSON object with information about each center
         *
         * @type {object}
         */
        this.reportActivityData = null;

        /**
         * Load helpers
         *
         * @type {Helpers}
         */
        this.helpers = new Helpers()

        /**
         * Import JSON data
         */
        this.helpers.fromJSON(DATA_CONFIG.REPORTS_ACTIVITY).then (response => {
            this.reportActivityData = response;
            this.init();
        });
    }

    /**
     * Initialize the view
     *
     * @method init
     */
    init() {
        this.isEnabled = false;
        this.currentIndex = 0;
        this.$contents = this.$element.find(CONFIG.CONTENTS).empty();
        this.$loadBtn = this.$element.find(CONFIG.LOAD_MORE_BTN);
        this.reportActivityData = this.formatData(this.reportActivityData);

        return this.setupHandlers()
                   .createChildren()
                   .layout()
                   .enable();
    }

    /**
     * Binds the scope of handler functions
     * Should be run once on view initialization
     *
     * @method setupHandlers
     * @returns {ReportsActivityView}
     */
    setupHandlers() {
        this.handleClick = this.onClick.bind(this);

        return this;
    }

    /**
     * Create any child objects and references to DOM elements
     * Should only be run once on init of the view
     *
     * @method createChildren
     * @returns {ReportsActivityView}
     */
    createChildren() {
        return this;
    }

    /**
     * Update the DOM to reflect the current state of the model
     *
     * @method layout
     * @returns {ReportsActivityView}
     */
    layout() {
        this.populateList(this.reportActivityData);

        return this;
    }

    /**
     * Disable, update the DOM, and then re-enable to create handlers for new DOM
     *
     * @method render
     */
    render() {
        this.disable();
        this.layout();
        this.enable();
    }

    /**
     * Enables the view
     * Performs binding to handlers
     * Exits early if already enabled
     *
     * @method enable
     * @returns {ReportsActivityView}
     */
    enable() {
        if (this.isEnabled) {
            return this;
        }

        this.isEnabled = true;

        for (let i = 0; i < this.$loadBtn.length; i++) {
            this.$loadBtn[i].addEventListener('click', this.handleClick);
        }

        return this;
    }

    /**
     * Disables the view
     * Tears down any event binding to handlers
     * Exits early if it is already disabled
     *
     * @method disable
     * @returns {ReportsActivityView}
     */
    disable() {
        if (!this.isEnabled) {
            return this;
        }

        for (let i = 0; i < this.$loadBtn.length; i++) {
            this.$loadBtn[i].removeEventListener('click', this.handleClick);
        }

        this.isEnabled = false;

        return this;
    }

    /**
     * Destroys the view
     * Tears down any events, handlers, elements
     * Should be called when the object should be left unused
     *
     * @method destroy
     * @returns {ReportsActivityView}
     */
    destroy() {
        this.disable();

        return this;
    }

    /**
     * Reformat data to create readable dates and file types
     *
     * @param object
     * @returns {Object|*}
     */
    formatData(object) {
        object = this.reformatDates(object);
        object = this.extractFileNames(object);

        return object;
    }

    /**
     * Takes in reports activity data and re-formats the dates to be relative when relevant
     *
     * @param object
     * @returns {object}
     */
    reformatDates(object) {
        let reformattedDates = object;
        const twoDaysAgo = moment().subtract(2, 'days').endOf('day');

        _.forEach(reformattedDates, (value) => {
            let relativeDate = moment(value.date);

            value.date = moment(value.date).format('MMMM D YYYY');

            if (relativeDate.isAfter(twoDaysAgo)) {
                relativeDate = relativeDate.fromNow();
            } else {
                relativeDate = relativeDate.format('MMMM D YYYY');
            }

            value.relativeDate = relativeDate;
        });

        return reformattedDates;
    }

    /**
     * Look at the file URL and extract the file name, if present, into a new item in the object
     *
     * @param object
     * @returns {object}
     */
    extractFileNames(object) {
        let objectWithFileNames = object;
        _.forEach(objectWithFileNames, (value) => {
            let fileName = value.url;
            if (fileName.split('.').pop() !== fileName) {
                value.fileType = fileName.split('.').pop().toUpperCase();
                value.fileName = fileName.split('\\').pop().split('/').pop();
            }
        });

        return objectWithFileNames;
    }

    /**
     * Return a list of first N items
     *
     * @param objects
     * @returns {ReportsActivityView}
     */
    populateList(objects) {
        let objectsToLoad = objects;

        if (this.currentIndex === 0) {
            objectsToLoad = objects.slice(0, CONFIG.PAGE_LOAD_DEFAULT);
            this.currentIndex += CONFIG.PAGE_LOAD_DEFAULT;
        } else {
            objectsToLoad = objects.slice(this.currentIndex, (this.currentIndex + CONFIG.VIEW_MORE_COUNT));
            this.currentIndex += CONFIG.VIEW_MORE_COUNT;
        }

        this.generateActivityMarkup(objectsToLoad);

        return this;
    }

    /**
     * Generate markup for list of results activity
     *
     * @param objects
     * @returns {ReportsActivityView}
     */
    generateActivityMarkup(objects) {
        let reportsLayout = '';

        if (objects.length > 0) {
            _.forEach(objects, (value) => {
                reportsLayout += this.reportsActivityTemplate(value);
            });
        } else {
            reportsLayout += CONFIG.NO_RESULTS;
            this.$loadBtn.remove();
        }

        this.$contents.append(reportsLayout);
        if (this.currentIndex > CONFIG.PAGE_LOAD_DEFAULT) {
            this.$contents.find(` > *:gt(${this.currentIndex - CONFIG.VIEW_MORE_COUNT - 1})`).hide().slideDown('fast');
        }

        return this;
    }

    /**
     * Handles open click event
     *
     * @returns {ReportsActivityView}
     */
    onClick() {
        this.render();

        return this;
    }
}
